import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import type { DateInvite, Pick as PickAB, PlannerPatch } from '@/api/types';
import { endpoints } from '@/api';
import { qk } from '@/api/queries';
import { useAuth } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';
import { PRESS } from '@/components/fun';
import { DateTicket } from './DateTicket';
import { SlotBuilder } from './SlotBuilder';
import { dateInviteError } from './errors';
import { SPICE, VIBES, usePlannerCatalog, vibeOf } from './plannerData';
import { MAX_SUGGESTED, hostSlots, roundMinute, slotLabel } from './slots';
import type { VenueChoice } from '@/api/types';
import { NO_THEME, VENUES, venueOf } from '@/features/venues/catalog';
import { VenuePicker } from '@/features/venues/VenuePicker';

export type CardId = 'when' | 'vibe' | 'spice' | 'picks' | 'prop' | 'topics' | 'venue';
const ALL: CardId[] = ['when', 'vibe', 'spice', 'picks', 'prop', 'topics', 'venue'];

/**
 * Which cards someone plays: the guest all of them, the host from the vibe
 * on; after a reschedule only "When works?". v9: "Where are we going?" closes
 * the deck while venues are on (the invite then carries a venue).
 */
export function deckFor(inv: DateInvite, onlyWhen = false): CardId[] {
  const deck = ALL.filter((c) => c !== 'venue' || !!inv.venue);
  if (inv.role === 'host') return deck.slice(1);
  if (onlyWhen) return ['when'];
  return (inv.slots ?? []).length ? deck : deck.slice(1);
}

/** The guest still has to say which times work (fresh, or after a reschedule). */
export function needsTimes(inv: DateInvite): boolean {
  return inv.role === 'guest' && inv.status === 'planning';
}

/** Keep every cache that holds this invite fresh. */
export function useStoreInvite() {
  const qc = useQueryClient();
  return (v: DateInvite) => {
    qc.setQueryData(qk.dateInvite(v.id), (prev: DateInvite | undefined) => (prev ? { ...prev, ...v } : v));
    qc.setQueryData<{ invites: DateInvite[] }>(qk.dateInvites, (p) => (p ? { invites: p.invites.map((x) => (x.id === v.id ? v : x)) } : p));
  };
}

function CardShell({ id, title, sub, children }: { id: CardId; title: string; sub?: string; children: ReactNode }) {
  return (
    <div data-testid={`planner-card-${id}`}>
      <h3 className="text-balance text-[22px] font-semibold leading-tight">{title}</h3>
      {sub && <p className="mt-1 text-pretty text-[15px] text-muted">{sub}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

/** A "yes" moment: a green sticker slapped on the card. */
function Burst({ text }: { text: string }) {
  const reduce = useReducedMotion();
  return (
    <motion.p
      initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, rotate: -2 }}
      animate={{ opacity: 1, scale: 1, rotate: -2 }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
      className="mt-4 inline-flex rounded-[20px] bg-green px-4 py-2 text-[15px] font-semibold leading-snug text-night"
      role="status"
      data-testid="planner-burst"
    >
      {text}
    </motion.p>
  );
}

/**
 * "Plan our date" (SPEC_V6_2 §2): a deck of one-tap, skippable cards that
 * builds a date ticket. Every answer is saved as you go (partial saves).
 */
export function PlannerDeck({ invite, onlyWhen = false, onFinish, finale }: { invite: DateInvite; onlyWhen?: boolean; onFinish?: (inv: DateInvite) => void; finale?: (inv: DateInvite) => ReactNode }) {
  const reduce = useReducedMotion();
  const me = useAuth((s) => s.me);
  const store = useStoreInvite();
  const { props, topics } = usePlannerCatalog();
  const cards = useMemo(() => deckFor(invite, onlyWhen), [invite.role, invite.slots?.length, !!invite.venue, onlyWhen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [inv, setInv] = useState(invite);
  const [i, setI] = useState(0);
  const [pending, setPending] = useState<PlannerPatch | null>(null);
  const busy = !!pending;
  const [err, setErr] = useState<string | null>(null);
  const [burst, setBurst] = useState<string | null>(null);
  const mine = inv.planner?.mine ?? {};

  // Card-local state.
  const [slotsOk, setSlotsOk] = useState<string[]>(mine.slots_ok ?? []);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState<Date[]>((mine.suggested ?? []).map((s) => new Date(s)));
  const [spice, setSpice] = useState<number>(mine.spice ?? 1);
  const [picks, setPicks] = useState<PickAB[]>(mine.picks?.length === 3 ? mine.picks : []);
  const [love, setLove] = useState<string[]>(mine.topics_love ?? []);
  const [skip, setSkip] = useState<string>(mine.topic_skip ?? '');
  const [skipMode, setSkipMode] = useState(false);
  const done = i >= cards.length;
  const card = cards[i];

  const advance = (after = reduce ? 0 : 320) => {
    window.setTimeout(() => {
      setBurst(null);
      setI((n) => n + 1);
    }, after);
  };

  const save = async (patch: PlannerPatch): Promise<DateInvite | null> => {
    if (pending) return null;
    setPending(patch);
    setErr(null);
    try {
      const v = await endpoints.saveDatePlanner(inv.id, patch);
      setInv(v);
      store(v);
      return v;
    } catch (e) {
      setErr(dateInviteError(e));
      return null;
    } finally {
      setPending(null);
    }
  };

  // Reaching the ticket marks the planner done (once).
  const finished = useRef(false);
  useEffect(() => {
    if (!done || finished.current) return;
    finished.current = true;
    void save({ done: true }).then((v) => v && onFinish?.(v));
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  const timesLocked = inv.status !== 'planning' && inv.status !== 'awaiting_host';
  const hs = hostSlots(inv);
  const pairs = inv.planner?.pairs ?? [];
  const pickIdx = Math.min(picks.length, 2);

  let body: ReactNode = null;
  switch (card) {
    case 'when':
      body = (
        <CardShell id="when" title="Tap every time that works" sub="Pick as many as you like. None work? Suggest your own.">
          {timesLocked ? (
            <p className="text-[15px] text-muted">The time is already set. On to the fun part.</p>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Proposed times">
                {hs.map((s, n) => {
                  const on = slotsOk.includes(s.id);
                  const l = slotLabel(s.starts_at);
                  return (
                    <motion.button
                      key={s.id}
                      type="button"
                      {...PRESS}
                      aria-pressed={on}
                      disabled={busy}
                      onClick={() => setSlotsOk((cur) => (on ? cur.filter((x) => x !== s.id) : [...cur, s.id]))}
                      className={cn(
                        'relative rounded-card p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg disabled:opacity-60',
                        on ? 'bg-night text-bone' : 'bg-raised hover:bg-line',
                      )}
                      data-testid={`planner-slot-${n}`}
                    >
                      <span className={cn('block text-[13px] font-medium', on ? 'text-bone/75' : 'text-muted')}>{l.day}</span>
                      <span className="block text-[22px] font-semibold tabular-nums leading-tight">{l.time}</span>
                      <span className={cn('block text-[13px] tabular-nums', on ? 'text-bone/75' : 'text-muted')}>{s.length_min} min</span>
                      <span className={cn('absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full', on ? 'bg-bone text-night' : 'border-2 border-muted/50')} aria-hidden>
                        {on && <Icon name="check" size={14} />}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
              <div className="mt-4">
                {suggesting ? (
                  <div className="rounded-card bg-ground p-4">
                    <p className="mb-3 text-[15px] font-semibold">Suggest up to {MAX_SUGGESTED} other times</p>
                    <SlotBuilder value={suggested} onChange={setSuggested} max={MAX_SUGGESTED} chips={false} testId="planner-suggest" />
                  </div>
                ) : (
                  <Button variant="ghost" icon="plus" onClick={() => setSuggesting(true)} data-testid="planner-suggest-toggle">
                    Suggest another time
                  </Button>
                )}
              </div>
              <Button
                size="lg"
                block
                className="mt-4"
                iconRight="chevron-right"
                loading={busy}
                disabled={slotsOk.length === 0 && suggested.length === 0}
                onClick={async () => {
                  const v = await save({ slots_ok: slotsOk, suggested: suggested.map((d) => roundMinute(d).toISOString()) });
                  if (!v) return;
                  if (v.status === 'scheduled') setBurst('It’s a date!');
                  advance(v.status === 'scheduled' ? 900 : undefined);
                }}
                data-testid="planner-when-save"
              >
                {slotsOk.length + suggested.length === 0 ? 'Pick at least one' : slotsOk.length + suggested.length === 1 ? 'This one works' : `These ${slotsOk.length + suggested.length} work`}
              </Button>
            </>
          )}
        </CardShell>
      );
      break;
    case 'vibe':
      body = (
        <CardShell id="vibe" title="What kind of evening?" sub="No wrong answers. Except pineapple on pizza. Kidding.">
          <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Vibe">
            {VIBES.map((v) => {
              const on = mine.vibe === v.id;
              return (
                <motion.button
                  key={v.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  {...PRESS}
                  disabled={busy}
                  aria-busy={pending?.vibe === v.id || undefined}
                  onClick={async () => {
                    const r = await save({ vibe: v.id });
                    if (!r) return;
                    const match = r.planner?.vibe_match;
                    if (match) setBurst(`You both went ${v.label}!`);
                    advance(match ? 1100 : undefined);
                  }}
                  className={cn(
                    'flex flex-col gap-0.5 rounded-card px-5 py-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg disabled:opacity-60 sm:min-h-[120px] sm:justify-end sm:px-4',
                    on ? 'bg-night text-bone' : 'bg-raised hover:bg-line',
                  )}
                  data-testid={`planner-vibe-${v.id}`}
                >
                  <span className="text-[17px] font-semibold leading-tight">{v.label}</span>
                  <span className={cn('text-[13px] leading-snug', on ? 'text-bone/75' : 'text-muted')}>{v.line}</span>
                </motion.button>
              );
            })}
          </div>
        </CardShell>
      );
      break;
    case 'spice':
      body = (
        <CardShell id="spice" title="How spicy can questions get?" sub="Private — they never see this. We use the milder of your two answers.">
          <div className="rounded-card bg-ground p-4">
            <div className="grid grid-cols-3 gap-1.5" aria-hidden data-testid="planner-spice-peppers" data-level={spice}>
              {[1, 2, 3].map((n) => (
                <span key={n} className={cn('h-3 rounded-full transition-colors duration-150', n <= spice ? 'bg-red' : 'bg-raised')} />
              ))}
            </div>
            <p className="mt-4 text-[17px] font-semibold">{SPICE[spice].label}</p>
            <p className="text-[15px] text-muted">{SPICE[spice].line}</p>
            <input
              type="range"
              min={0}
              max={3}
              step={1}
              value={spice}
              onChange={(e) => setSpice(Number(e.target.value))}
              aria-label="Spice level"
              aria-valuetext={SPICE[spice].label}
              className="mt-4 w-full accent-fg"
              data-testid="planner-spice-range"
            />
            <div className="mt-1 flex justify-between text-[13px] text-muted" aria-hidden>
              <span>Sweet</span>
              <span>Spicy</span>
            </div>
          </div>
          <Button
            size="lg"
            block
            className="mt-4"
            icon="check"
            loading={busy}
            onClick={async () => {
              if (await save({ spice })) advance();
            }}
            data-testid="planner-spice-save"
          >
            Lock it in
          </Button>
        </CardShell>
      );
      break;
    case 'picks':
      body = (
        <CardShell id="picks" title={pairs.length ? `This or that: ${pickIdx + 1} of 3` : 'This or that'} sub="Go with your gut. We’ll compare once you’ve both played.">
          {pairs.length === 3 && picks.length < 3 ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={pickIdx}
                initial={reduce ? { opacity: 0 } : { opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, x: -12, transition: { duration: 0.15, ease: 'easeOut' } }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="grid grid-cols-[1fr_auto_1fr] items-center gap-2"
              >
                {(['a', 'b'] as const).map((side, n) => (
                  <motion.button
                    key={side}
                    type="button"
                    {...PRESS}
                    disabled={busy}
                    aria-busy={pending?.picks?.[2] === side || undefined}
                    onClick={async () => {
                      const next = [...picks, side];
                      setPicks(next);
                      if (next.length === 3) {
                        const r = await save({ picks: next });
                        if (!r) return setPicks(picks);
                        const m = r.planner?.picks_matched;
                        if (m != null) setBurst(`You matched on ${m}/3`);
                        advance(m != null ? 1100 : undefined);
                      }
                    }}
                    className={cn(
                      'min-h-[96px] rounded-card bg-raised p-3 text-[17px] font-semibold leading-tight transition-colors hover:bg-line focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg disabled:opacity-60',
                      n === 1 && 'col-start-3',
                    )}
                    data-testid={`planner-pick-${pickIdx}-${side}`}
                  >
                    {side === 'a' ? pairs[pickIdx].a : pairs[pickIdx].b}
                  </motion.button>
                ))}
                <span className="col-start-2 row-start-1 text-[13px] text-muted" aria-hidden>
                  or
                </span>
              </motion.div>
            </AnimatePresence>
          ) : (
            <div className="space-y-3">
              <p className="text-[15px] text-muted">{picks.length === 3 ? 'Sealed. Want to redo them?' : 'Nothing to pick here.'}</p>
              {picks.length === 3 && (
                <Button variant="subtle" icon="flip" onClick={() => setPicks([])} data-testid="planner-picks-redo">
                  Redo
                </Button>
              )}
            </div>
          )}
        </CardShell>
      );
      break;
    case 'prop':
      body = (
        <CardShell id="prop" title="What are you bringing?" sub="It shows on your ticket — and might earn you a show-and-tell.">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Prop">
            {props.map((p) => {
              const on = mine.prop === p.id;
              return (
                <motion.button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  {...PRESS}
                  disabled={busy}
                  aria-busy={pending?.prop === p.id || undefined}
                  onClick={async () => {
                    if (await save({ prop: p.id })) advance();
                  }}
                  className={cn(
                    'flex h-14 items-center rounded-full px-5 text-left text-[15px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg disabled:opacity-60',
                    on ? 'bg-night text-bone' : 'bg-raised hover:bg-line',
                  )}
                  data-testid={`planner-prop-${p.id}`}
                >
                  <span className="first-letter:uppercase">{p.label}</span>
                </motion.button>
              );
            })}
          </div>
        </CardShell>
      );
      break;
    case 'topics':
      body = (
        <CardShell
          id="topics"
          title={skipMode ? 'Anything to skip?' : 'Pick two things to talk about'}
          sub={skipMode ? 'Private — they never see this. We just won’t bring it up.' : 'They’ll see these on your ticket.'}
        >
          <div className="flex flex-wrap gap-2" role="group" aria-label={skipMode ? 'Topic to skip' : 'Topics to talk about'}>
            {topics.map((t) => {
              const loved = love.includes(t.id);
              const skipped = skip === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={skipMode ? skipped : loved}
                  disabled={busy || (skipMode ? loved : !loved && love.length >= 2)}
                  onClick={() => {
                    if (skipMode) setSkip(skipped ? '' : t.id);
                    else setLove((cur) => (loved ? cur.filter((x) => x !== t.id) : [...cur, t.id]));
                  }}
                  className={cn(
                    'inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-[15px] font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg disabled:opacity-35',
                    loved ? 'bg-primary text-ground' : skipped ? 'bg-danger text-ground line-through' : 'bg-raised hover:bg-line',
                  )}
                  data-testid={`planner-topic-${t.id}`}
                >
                  {loved && <Icon name="check" size={16} />}
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-[auto_1fr]">
            {!skipMode ? (
              <Button variant="ghost" icon="x" onClick={() => setSkipMode(true)} data-testid="planner-topics-skipmode">
                Skip one topic
              </Button>
            ) : (
              <Button variant="ghost" icon="chevron-left" onClick={() => setSkipMode(false)}>
                Back to favourites
              </Button>
            )}
            <Button
              size="lg"
              icon="check"
              loading={busy}
              onClick={async () => {
                if (await save({ topics_love: love, topic_skip: skip })) advance();
              }}
              data-testid="planner-topics-save"
            >
              {love.length === 0 ? 'Surprise me' : 'Done'}
            </Button>
          </div>
        </CardShell>
      );
      break;
    case 'venue': {
      const hostName = inv.role === 'host' ? 'You' : inv.host.display_name;
      const guestVote = inv.role === 'host' ? inv.planner?.theirs?.venue : mine.venue;
      const pickedBy = (v: VenueChoice) =>
        [v === inv.venue ? (inv.role === 'host' ? 'You' : inv.host.display_name) : null, inv.role === 'host' && v === guestVote ? (inv.guest?.display_name ?? 'Them') : null].filter(Boolean).join(' + ');
      const badges = Object.fromEntries(
        [NO_THEME, ...VENUES.map((v) => v.id)].map((id) => [id, pickedBy(id) ? <span className="rounded-full bg-fg px-2 py-0.5 text-[12px] font-semibold text-ground">{pickedBy(id)}</span> : null]),
      ) as Partial<Record<VenueChoice, ReactNode>>;
      body = (
        <CardShell
          id="venue"
          title={inv.role === 'host' ? 'Set the scene' : 'Where are we going?'}
          sub={
            inv.role === 'host'
              ? 'The look, the sounds and the photo strip of your date. They can vote — on a tie, your pick wins.'
              : `${hostName} picked ${inv.venue === NO_THEME ? 'no theme' : (venueOf(inv.venue)?.short ?? 'a spot')}. Vote for yours — if you differ, ${hostName}’s pick wins this time.`
          }
        >
          <VenuePicker
            value={inv.role === 'host' ? inv.venue : (mine.venue ?? null)}
            disabled={busy}
            testId="planner-venue"
            label="Where are we going?"
            badges={inv.role === 'guest' ? badges : guestVote ? badges : undefined}
            onPick={async (v) => {
              const r = await save({ venue: v });
              if (!r) return;
              const hostPick = r.venue ?? null;
              const label = hostPick === NO_THEME ? 'no theme' : venueOf(hostPick)?.short;
              const vote = r.role === 'host' ? r.planner?.theirs?.venue : r.planner?.mine.venue;
              if (vote && hostPick && vote === hostPick) setBurst(`You both picked ${label}!`);
              else if (vote && hostPick && r.role === 'guest') setBurst(`A tie — ${r.host.display_name}’s pick wins. Next date, you choose.`);
              advance(vote ? 1300 : undefined);
            }}
          />
        </CardShell>
      );
      break;
    }
  }

  const other = inv.role === 'host' ? inv.guest : inv.host;
  return (
    <section className="relative rounded-card bg-surface p-5 sm:p-6" aria-label="Plan our date" data-testid="planner-deck" data-card={card ?? 'ticket'}>
      {!done && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex gap-1.5" aria-label={`Card ${i + 1} of ${cards.length}`} role="progressbar" aria-valuemin={1} aria-valuemax={cards.length} aria-valuenow={i + 1}>
            {cards.map((c, n) => (
              <span key={c} className={cn('h-1.5 rounded-full transition-[width,background-color] duration-150', n < i ? 'w-5 bg-fg' : n === i ? 'w-8 bg-fg' : 'w-5 bg-line')} />
            ))}
          </div>
          <div className="flex items-center gap-1">
            {i > 0 && (
              <button type="button" onClick={() => setI((n) => Math.max(0, n - 1))} disabled={busy} className="h-9 rounded-full px-3 text-[15px] font-medium text-muted transition-colors hover:text-fg" data-testid="planner-back">
                Back
              </button>
            )}
            <button type="button" onClick={() => advance(0)} disabled={busy} className="h-9 rounded-full px-3 text-[15px] font-medium text-muted transition-colors hover:text-fg" data-testid="planner-skip">
              {card === 'when' ? 'Decide later' : 'Skip'}
            </button>
          </div>
        </div>
      )}
      <AnimatePresence mode="wait" initial={false}>
        {!done ? (
          <motion.div
            key={card}
            initial={reduce ? { opacity: 0 } : { opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, x: -12, transition: { duration: 0.15, ease: 'easeOut' } }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {body}
            <AnimatePresence>{burst && <Burst text={burst} />}</AnimatePresence>
          </motion.div>
        ) : (
          <motion.div key="ticket" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="planner-finale">
            <h3 className="text-[22px] font-semibold leading-tight">{other ? `You × ${other.display_name}` : 'Ready when they are'}</h3>
            <p className="mb-4 mt-1 text-[15px] text-muted">Your date ticket</p>
            <DateTicket inv={inv} owner={me} answers={inv.planner?.mine} title={vibeOf(inv.planner?.mine.vibe) ? `A ${vibeOf(inv.planner?.mine.vibe)!.label.toLowerCase()} first date` : 'A first date'} testId="planner-ticket" />
            {finale?.(inv)}
          </motion.div>
        )}
      </AnimatePresence>
      {err && (
        <p role="alert" className="mt-4 text-[15px] font-medium text-danger" data-testid="planner-error">
          {err}
        </p>
      )}
    </section>
  );
}

/** The deck in a sheet (host's optional planner, the lobby's "Plan our date"). */
export function PlannerSheet({ invite, onClose, onlyWhen }: { invite: DateInvite | null; onClose: () => void; onlyWhen?: boolean }) {
  return (
    <Sheet open={!!invite} onClose={onClose} title={invite?.role === 'host' ? 'Plan your side' : 'Plan our date'} description="One tap per card. Skip anything." testId="planner-sheet" maxHeight="92vh">
      {invite && (
        <PlannerDeck
          invite={invite}
          onlyWhen={onlyWhen}
          finale={() => (
            <Button size="lg" block className="mt-4" onClick={onClose} data-testid="planner-close">
              Looks good
            </Button>
          )}
        />
      )}
    </Sheet>
  );
}
